import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Breadcrumbs, Theme, Typography } from "@mui/material";
import { TextLink } from "@shared/TextLink";

const useStyles = makeStyles()((theme: Theme) => {
    const { fontFamily, fontSize, fontWeight, letterSpacing, lineHeight } = theme.typography.body1;

    return {
        breadcrumbs: {
            fontFamily,
            fontSize,
            fontWeight,
            letterSpacing,
            lineHeight,
            "& .MuiBreadcrumbs-li": {
                display: "inline-flex",
                alignItems: "center",
            },
        },
        crumb: {
            display: "inline-flex",
            alignItems: "center",
            margin: 0,
            padding: 0,
        },
        text: {
            color: theme.palette.emteria.text.primary,
        },
    };
});

type Props = {
    groups: API.Group[];
    workspaceLabel: string;
    onSelect?: (groupId: number | null) => void;
};

export const GroupBreadcrumbs = ({ groups, onSelect, workspaceLabel }: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common"]);

    if (!onSelect) {
        if (groups.length === 0) {
            return (
                <Breadcrumbs className={classes.breadcrumbs}>
                    <Typography variant="body1" component="span" className={cx(classes.crumb, classes.text)}>
                        {workspaceLabel}
                    </Typography>
                </Breadcrumbs>
            );
        }

        return (
            <Breadcrumbs className={classes.breadcrumbs}>
                <Typography variant="body1" component="span" className={cx(classes.crumb, classes.text)}>
                    {workspaceLabel}
                </Typography>
                {groups.map((group) => (
                    <Typography key={group.id} variant="body1" component="span" className={cx(classes.crumb, classes.text)}>
                        {group.name}
                    </Typography>
                ))}
            </Breadcrumbs>
        );
    }

    if (groups.length === 0) {
        return (
            <Breadcrumbs className={classes.breadcrumbs}>
                <Typography variant="body1" component="span" className={cx(classes.crumb, classes.text)}>
                    {workspaceLabel}
                </Typography>
            </Breadcrumbs>
        );
    }

    return (
        <Breadcrumbs className={classes.breadcrumbs}>
            <TextLink
                variant="body1"
                component="button"
                type="button"
                className={classes.crumb}
                onClick={() => onSelect(null)}
                tooltip={t("common:tooltip.showWorkspaceRoot")}
            >
                {workspaceLabel}
            </TextLink>
            {groups.map((group, index) => {
                const isLast = index === groups.length - 1;
                if (isLast) {
                    return (
                        <Typography key={group.id} variant="body1" component="span" className={cx(classes.crumb, classes.text)}>
                            {group.name}
                        </Typography>
                    );
                }
                return (
                    <TextLink
                        key={group.id}
                        variant="body1"
                        component="button"
                        type="button"
                        className={classes.crumb}
                        onClick={() => onSelect(group.id)}
                        tooltip={t("common:tooltip.showGroupDetails")}
                    >
                        {group.name}
                    </TextLink>
                );
            })}
        </Breadcrumbs>
    );
};
